import React, { useState } from 'react';
import { functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { getXpGain } from '../../utils/LevelSystem';
import { STokenLogo } from '../StreamDisplay';
import { CommonChestIcon, UncommonChestIcon, RareChestIcon, EpicChestIcon, LegendaryChestIcon } from '../ChestIcons';
import './SlotMachine.css';

const CHEST_ICON_MAP = {
  Common:    CommonChestIcon,
  Uncommon:  UncommonChestIcon,
  Rare:      RareChestIcon,
  Epic:      EpicChestIcon,
  Legendary: LegendaryChestIcon,
  Ultimate:  LegendaryChestIcon,
};

const RARITIES = [
  { name: 'Common',    color: '#d4d4d4' },
  { name: 'Uncommon',  color: '#2ecc71' },
  { name: 'Rare',      color: '#3498db' },
  { name: 'Epic',      color: '#bf5fff' },
  { name: 'Legendary', color: '#ffaa00' },
  { name: 'Ultimate',  color: '#ffd700' },
];

const WINNING_ODDS = [
  { combo: ['Common','Common','Common'],         weight: 10, reward: 350,   label: '3× Common' },
  { combo: ['Uncommon','Uncommon','Uncommon'],   weight: 10, reward: 450,   label: '3× Uncommon' },
  { combo: ['Rare','Rare','Rare'],               weight: 8,  reward: 800,   label: '3× Rare' },
  { combo: ['Epic','Epic','Epic'],               weight: 6,  reward: 2300,  label: '3× Epic' },
  { combo: ['Legendary','Legendary','Legendary'],weight: 5,  reward: 8000,  label: '3× Legendary' },
  { combo: ['Ultimate','Ultimate','Ultimate'],    weight: 3,  reward: 10000, label: '3× Ultimate' },
  { combo: ['Common','Common','Uncommon'],       weight: 26, reward: 300,   label: '2× Common' },
  { combo: ['Uncommon','Uncommon','Rare'],       weight: 21, reward: 525,   label: '2× Uncommon' },
  { combo: ['Rare','Rare','Epic'],               weight: 8,  reward: 1200,  label: '2× Rare' },
  { combo: ['Epic','Epic','Legendary'],          weight: 3,  reward: 5200,  label: '2× Epic' },
];

const LOSING_COMBOS = [
  ['Common','Uncommon','Rare'],
  ['Common','Uncommon','Epic'],
  ['Common','Rare','Epic'],
  ['Uncommon','Rare','Epic'],
  ['Uncommon','Epic','Legendary'],
  ['Rare','Epic','Legendary'],
  ['Common','Common','Epic'],
  ['Uncommon','Uncommon','Legendary'],
  ['Rare','Rare','Uncommon'],
  ['Epic','Epic','Rare'],
];

function getRandomOutcome() {
  const roll = Math.random();

  if (roll >= 0.65) {
    // 35 % — losing spin
    const combo = LOSING_COMBOS[Math.floor(Math.random() * LOSING_COMBOS.length)];
    return { combo, reward: 0, label: 'No Match', win: false };
  }

  // 65 % — winning spin. w is uniform [0, 1) within the win pool.
  const w = Math.random();

  // Thresholds expressed as cumulative overall-probability / 0.65.
  // Ultimate  0.00001 % overall = 0.0000001; cumulative = 0.0000001
  // Legendary 0.0001 % overall  = 0.000001;  cumulative = 0.0000011
  if (w < 0.0000001 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '3× Ultimate'),   win: true };
  if (w < 0.0000011 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '3× Legendary'),  win: true };
  if (w < 0.0030011 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '2× Epic'),        win: true };
  if (w < 0.0100011 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '3× Epic'),        win: true };
  if (w < 0.0250011 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '2× Rare'),        win: true };
  if (w < 0.0550011 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '3× Rare'),        win: true };
  if (w < 0.1450011 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '2× Uncommon'),    win: true };
  if (w < 0.2850011 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '3× Uncommon'),    win: true };
  if (w < 0.4550011 / 0.65) return { ...WINNING_ODDS.find(o => o.label === '2× Common'),      win: true };
  return { ...WINNING_ODDS.find(o => o.label === '3× Common'), win: true };
}

function ReelSlot({ rarity }) {
  const r = RARITIES.find(x => x.name === rarity) || RARITIES[0];
  const Icon = CHEST_ICON_MAP[rarity] || CommonChestIcon;
  return (
    <div className="slot__reel" style={{ '--reel-color': r.color }}>
      <div className="slot__reel-inner">
        <Icon size={52} />
        <span className="slot__reel-label">{rarity}</span>
      </div>
    </div>
  );
}

export default function SlotMachine({ onClose }) {
  const [spinning, setSpinning] = useState(false);
  const [reels, setReels]       = useState(['Common', 'Common', 'Common']);
  const [result, setResult]     = useState(null);

  const spin = async () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);

    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 50));
      setReels(RARITIES.slice().sort(() => Math.random() - 0.5).slice(0, 3).map(r => r.name));
    }

    const outcome = getRandomOutcome();
    setReels(outcome.combo);

    const xp = getXpGain('slot-machine');
    const netStreams = outcome.win ? outcome.reward - 325 : -325;
    httpsCallable(functions, 'creditGameResult')({ game: 'slots', netStreams, xp })
      .catch(e => console.error('[Slots]', e));

    setResult(outcome);
    setSpinning(false);
  };

  return (
    <div className="slot">
      <h2 className="slot__title">Slot Machine</h2>
      <p className="slot__cost" style={{ display:'flex', alignItems:'center', gap:4 }}>350<STokenLogo size={13} /> / spin</p>

      <div className="slot__reels">
        {reels.map((r, i) => <ReelSlot key={i} rarity={r} />)}
      </div>

      {result && (
        <div className={`slot__result${result.win ? ' slot__result--win' : ' slot__result--lose'}`}>
          {result.win ? (
            <>
              <STokenLogo size={24} />
              <span>+{result.reward} {result.label}</span>
            </>
          ) : (
            <span>No match — try again</span>
          )}
        </div>
      )}

      <button className="btn btn--primary btn--full" onClick={spin} disabled={spinning}>
        {spinning ? 'Spinning…' : <span style={{ display:'flex', alignItems:'center', gap:5, justifyContent:'center' }}>Spin · 350<STokenLogo size={15} /></span>}
      </button>
    </div>
  );
}
